import React, { useMemo, useState } from 'react';
import { Box, Chip, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import TimelineIcon from '@mui/icons-material/Timeline';
import { EntityType, PropertyDefinition } from '../types';
import { PositionLockButton } from './PositionLockButton';
import { useGetRepositoryEntriesByFilterQuery } from '../services/repositoryEntries';
import { useAppSelector } from '../hooks';
import { selectEntryReadableValues } from '../containers/contentSlice';

type StaticFilterChip = {
	slug: string;
	kind: 'static';
	label: string;
};

type BelongsToFilterChip = {
	slug: string;
	kind: 'belongsTo';
	filterName: string;
	targetRepositoryId: string;
	targetEntryId: string;
};

type ActiveFilterChip = StaticFilterChip | BelongsToFilterChip;

interface RepositoryEntriesFilterAreaProps {
	resolvedDataFilter?: string;
	includeArchived: boolean;
	propertyDefinitions: PropertyDefinition[];
	visibleItemsCount: number;
	/** When set, a subset of table columns is selected (desktop); shows a chip. Omit or `null` when not applicable. */
	visibleTableFieldCount?: number | null;
	/** Omitted where filters don't apply (e.g. a multi-collection timeline); hides the item-count filter chip. */
	onOpenFilters?: () => void;
	onRemoveWhereFilter: (slug: string) => void;
	onIncludeArchivedChange: (value: boolean) => void;
	/** Clears table column subset (all columns). */
	onClearVisibleTableFields?: () => void;
	/** Present when a list/table view exists to switch back to (e.g. from a timeline). */
	onSelectListView?: () => void;
	/** Timeline card position lock, shown as a switch next to the list-view button. */
	positionLock?: PositionLock;
	/** Date fields the entries can be viewed as a timeline by, shown as a sort chip opening a menu of them. */
	timelineSort?: TimelineSort;
}

type PositionLock = { isLocked: boolean; onToggle: () => void };
type TimelineSort = { dateProperties: PropertyDefinition[]; onSelect: (def: PropertyDefinition) => void };

const BelongsToFilterValueChip = ({
	filterName,
	targetRepositoryId,
	targetEntryId,
	onDelete,
}: {
	filterName: string;
	targetRepositoryId: string;
	targetEntryId: string;
	onDelete: () => void;
}) => {
	// TODO: should fetch by id instead of whole repository
	const { data: targetEntriesData } = useGetRepositoryEntriesByFilterQuery(
		{ repositoryId: targetRepositoryId, includeArchived: true },
		{ skip: !targetRepositoryId.trim() }
	);
	const entryReadableLabels = useAppSelector(selectEntryReadableValues);
	const linkedEntryLabel = useMemo(() => {
		const targetEntry = (targetEntriesData?.rows ?? []).find((row) => row.id === targetEntryId);
		const fromApi = (targetEntry?.label ?? '').trim();
		if (fromApi) {
			return fromApi;
		}
		const fromLocal = (entryReadableLabels.get(targetEntryId) ?? '').trim();
		if (fromLocal) {
			return fromLocal;
		}
		return targetEntryId;
	}, [targetEntriesData?.rows, targetEntryId, entryReadableLabels]);

	return (
		<Chip
			label={`${filterName}: ${linkedEntryLabel}`}
			color="primary"
			variant="outlined"
			onDelete={onDelete}
		/>
	);
};

const RepositoryEntriesFilterArea = ({
	resolvedDataFilter,
	includeArchived,
	propertyDefinitions,
	visibleItemsCount,
	visibleTableFieldCount = null,
	onOpenFilters,
	onRemoveWhereFilter,
	onIncludeArchivedChange,
	onClearVisibleTableFields,
	onSelectListView,
	positionLock,
	timelineSort,
}: RepositoryEntriesFilterAreaProps) => {
	const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<HTMLElement | null>(null);
	const propertyMetaBySlug = useMemo(
		() =>
			new Map(propertyDefinitions.map((property) => [
				property.slug,
				{
					filterName: property.label?.trim() || property.slug,
					type: property.type,
					targetRepositoryId:
						property.type === EntityType.PropertyDefinitionBelongsTo
							? String((property as { targetRepositoryId?: string }).targetRepositoryId ?? '').trim()
							: '',
				},
			])),
		[propertyDefinitions]
	);

	const activeDataFilterChips = useMemo((): ActiveFilterChip[] => {
		if (!resolvedDataFilter) {
			return [];
		}
		try {
			const doc = JSON.parse(resolvedDataFilter) as Record<string, unknown>;
			return Object.entries(doc).map(([slug, rawSpec]) => {
				const meta = propertyMetaBySlug.get(slug);
				const filterName = meta?.filterName ?? slug;
				if (rawSpec && typeof rawSpec === 'object' && !Array.isArray(rawSpec)) {
					const spec = rawSpec as Record<string, unknown>;
					if (
						meta?.type === EntityType.PropertyDefinitionBelongsTo
						&& typeof spec.targetEntryId === 'string'
						&& spec.targetEntryId.trim()
						&& meta.targetRepositoryId
					) {
						return {
							slug,
							kind: 'belongsTo',
							filterName,
							targetRepositoryId: meta.targetRepositoryId,
							targetEntryId: spec.targetEntryId.trim(),
						} satisfies BelongsToFilterChip;
					}
					if (spec.isUnset === true) {
						return { slug, kind: 'static', label: `${filterName}: is unset` } satisfies StaticFilterChip;
					}
					const start = typeof spec.start === 'string' ? spec.start.trim().slice(0, 10) : '';
					const end = typeof spec.end === 'string' ? spec.end.trim().slice(0, 10) : '';
					if (start || end) {
						return {
							slug,
							kind: 'static',
							label: `${filterName}: ${start || '...'} - ${end || '...'}`,
						} satisfies StaticFilterChip;
					}
				}
				return { slug, kind: 'static', label: filterName } satisfies StaticFilterChip;
			});
		} catch {
			return [];
		}
	}, [resolvedDataFilter, propertyMetaBySlug]);

	const hasVisibleTableFieldSubset =
		typeof visibleTableFieldCount === 'number'
		&& visibleTableFieldCount > 0
		&& visibleTableFieldCount < propertyDefinitions.length;
	const visibleTableFieldsChipLabel =
		hasVisibleTableFieldSubset && visibleTableFieldCount === 1
			? '1 field selected'
			: hasVisibleTableFieldSubset
				? `${visibleTableFieldCount} fields selected`
				: '';
	const hasAnyFilter =
		activeDataFilterChips.length > 0 || includeArchived || hasVisibleTableFieldSubset;
	const visibleItemsCountLabel = `${visibleItemsCount} item${visibleItemsCount === 1 ? '' : 's'}`;

	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				gap: 1.5,
				pt: 1,
				pb: 1,
				mt: { xs: 8, sm: !hasAnyFilter ? 0 : 0 },
				flexDirection: { xs: 'column', md: 'row' },
				height: {xs: 'auto', sm: '56px'},
			}}
		>
			<Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" justifyContent="center" sx={{maxWidth: {xs: '100%', sm: '400px', md: '600px', lg: '1200px'}}}>
				{positionLock && <PositionLockButton isLocked={positionLock.isLocked} onToggle={positionLock.onToggle} />}
				{!hasAnyFilter && onOpenFilters && (
					<Chip
						onClick={onOpenFilters}
						label={visibleItemsCountLabel}
						variant="outlined"
						icon={<TuneIcon fontSize='small' />}
						sx={{px:1}}
					/>
				)}
				{activeDataFilterChips.map((chip) =>
					chip.kind === 'belongsTo' ? (
						<BelongsToFilterValueChip
							key={chip.slug}
							filterName={chip.filterName}
							targetRepositoryId={chip.targetRepositoryId}
							targetEntryId={chip.targetEntryId}
							onDelete={() => onRemoveWhereFilter(chip.slug)}
						/>
					) : (
						<Chip
							key={chip.slug}
							label={chip.label}
							color="primary"
							variant="outlined"
							onDelete={() => onRemoveWhereFilter(chip.slug)}
						/>
					)
				)}
				{includeArchived && (
					<Chip
						label="Include archived"
						color="primary"
						variant="outlined"
						onDelete={() => onIncludeArchivedChange(false)}
					/>
				)}
				{hasVisibleTableFieldSubset && onClearVisibleTableFields && (
					<Chip
						label={visibleTableFieldsChipLabel}
						color="primary"
						variant="outlined"
						onDelete={onClearVisibleTableFields}
					/>
				)}
				{hasAnyFilter && (
					<Chip icon={<FilterListIcon />} color="primary" variant={hasAnyFilter ? 'filled' : 'outlined'} label={visibleItemsCountLabel} onClick={onOpenFilters} />
				)}
				{timelineSort && (
					<>
						<Chip
							onClick={(e) => setSortMenuAnchorEl(e.currentTarget)}
							label="Sort"
							variant="outlined"
							icon={<SortIcon fontSize='small' />}
							sx={{px:1}}
						/>
						<Menu anchorEl={sortMenuAnchorEl} open={Boolean(sortMenuAnchorEl)} onClose={() => setSortMenuAnchorEl(null)}>
							{timelineSort.dateProperties.map((def) => (
								<MenuItem
									key={def.slug}
									onClick={() => {
										setSortMenuAnchorEl(null);
										timelineSort.onSelect(def);
									}}
								>
									<ListItemIcon><TimelineIcon fontSize='small' /></ListItemIcon>
									<ListItemText>{def.label}</ListItemText>
								</MenuItem>
							))}
						</Menu>
					</>
				)}
				{onSelectListView && (
					<Chip
						onClick={onSelectListView}
						label="View as list"
						variant="outlined"
						sx={{px:1}}
					/>
				)}
			</Stack>
		</Box>
	);
};

export default RepositoryEntriesFilterArea;
